// AgencyCard — collapsible card for one agent / agency with optional
// region → district → salons nesting and a salon-level table.
//
// Driven by props (data + columns config), styling matches the design.
//
//   columns: Array<{
//     field: string,
//     header: string,
//     width: string | number,        // CSS grid track (e.g. '120px' | '1.4fr' | 130)
//     align?: 'left' | 'right',
//     numeric?: boolean,             // monospace + right-align
//     sales?: boolean,               // 0 = textDim, >0 = good (green)
//     phone?: boolean,               // accent color, tel: link
//     render?: (value, row) => ReactNode  // custom renderer
//   }>
//
//   regions: Array<{
//     name: string,
//     stats: { salon_count, sales_in_period, sales_current_month, sales_yesterday },
//     districts?: Array<{ name, stats, salons }>,
//     salons?: Array<row>,           // when no districts (e.g. PLK)
//   }>
//
//   flatSalons (optional) — used when there's no region grouping (e.g. TMPL).
//
import React, { useState } from 'react'
import { theme as defaultTheme, T, fmt } from './tokens'
import { Icon } from './icons'
import { Pill } from './primitives'

export default function AgencyCard({
  agency, summary = {}, tags = [],
  expanded, onToggle,
  onSendReport, onSendScope,
  loading = false, error = null,
  regions = null, flatSalons = null,
  columns = [],
  emptyText = 'Brak salonów dla wybranych filtrów.',
  theme = defaultTheme,
  accentColor,
}) {
  const [openRegions, setOpenRegions] = useState({})
  const [openDistricts, setOpenDistricts] = useState({})

  const toggleRegion = (name) =>
    setOpenRegions(s => ({ ...s, [name]: !s[name] }))
  const toggleDistrict = (rname, dname) =>
    setOpenDistricts(s => ({ ...s, [`${rname}::${dname}`]: !s[`${rname}::${dname}`] }))

  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>
      {/* Header — agent row */}
      <button onClick={onToggle} style={{
        width: '100%',
        padding: '14px 18px',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        background: theme.bgSub, border: 'none',
        cursor: 'pointer', textAlign: 'left',
        borderBottom: expanded ? `1px solid ${theme.border}` : 'none',
        fontFamily: T.fontUI,
      }}>
        <span style={{
          color: theme.textDim, display: 'inline-flex',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 150ms',
        }}><Icon.chevron /></span>
        {accentColor && (
          <span style={{
            width: 4, height: 22, borderRadius: 2, background: accentColor,
            flexShrink: 0,
          }} />
        )}
        <span style={{
          fontSize: 15, fontWeight: 700, color: theme.text,
          letterSpacing: -0.2, minWidth: 160,
        }}>{agency}</span>
        {summary.rks && (
          <span style={{ fontSize: 12, color: theme.textSub }}>
            RKS:{' '}
            <span style={{ color: theme.text, fontWeight: 500 }}>{summary.rks}</span>
          </span>
        )}
        <div style={{ flex: 1 }} />
        {summary.salon_count != null && (
          <Pill theme={theme} kind="info">{fmt.int(summary.salon_count)} salonów</Pill>
        )}
        {summary.sales != null && (
          <Pill theme={theme} kind={summary.sales > 0 ? 'good' : 'neutral'}>
            Sprzedaż: {fmt.int(summary.sales)}
          </Pill>
        )}
        {summary.pos_type && (
          <Pill theme={theme} kind="neutral">{summary.pos_type}</Pill>
        )}
        {tags.map(t => <Pill key={t} theme={theme} kind="neutral">{t}</Pill>)}
        {onSendReport && (
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => { e.stopPropagation(); onSendReport(agency) }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.stopPropagation(); onSendReport(agency) } }}
            title="Wyślij raport"
            style={{
              marginLeft: 6, padding: 6, borderRadius: 6,
              color: theme.textSub, cursor: 'pointer',
              display: 'inline-flex',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = theme.surface; e.currentTarget.style.color = theme.accent }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = theme.textSub }}
          >
            <Icon.mail />
          </span>
        )}
      </button>

      {/* Body */}
      {expanded && (
        <div>
          {loading && (
            <div style={{ padding: 20, textAlign: 'center', color: theme.textSub, fontSize: 12.5 }}>
              Ładowanie…
            </div>
          )}
          {error && (
            <div style={{
              padding: '12px 18px',
              background: theme.badSub, color: theme.bad,
              fontSize: 12.5, fontWeight: 500,
            }}>{error}</div>
          )}
          {!loading && !error && (
            <>
              {/* Flat list (e.g. T-Mobile — no region/district grouping) */}
              {flatSalons && (
                flatSalons.length === 0
                  ? <EmptyRow theme={theme}>{emptyText}</EmptyRow>
                  : <SalonTable theme={theme} columns={columns} rows={flatSalons} />
              )}
              {/* Region → District → Salons */}
              {regions && regions.length === 0 && (
                <EmptyRow theme={theme}>{emptyText}</EmptyRow>
              )}
              {regions && regions.map((r) => (
                <RegionGroup key={r.name}
                  theme={theme}
                  region={r}
                  expanded={!!openRegions[r.name]}
                  onToggle={() => toggleRegion(r.name)}
                  openDistricts={openDistricts}
                  toggleDistrict={(d) => toggleDistrict(r.name, d)}
                  columns={columns}
                  onSendScope={onSendScope}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ─ Region (level 2) ────────────────────────────────────────────
function RegionGroup({ theme, region, expanded, onToggle, openDistricts, toggleDistrict, columns, onSendScope }) {
  const stats = region.stats || {}
  return (
    <div>
      <button onClick={onToggle} style={{
        width: '100%',
        padding: '10px 18px 10px 36px',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: 'transparent', border: 'none',
        cursor: 'pointer', textAlign: 'left',
        borderTop: `1px solid ${theme.border}`,
        borderBottom: expanded ? `1px solid ${theme.border}` : 'none',
        fontFamily: T.fontUI,
      }}>
        <span style={{ color: theme.bad, display: 'inline-flex' }}><Icon.pin /></span>
        <span style={{ fontSize: 13, fontWeight: 600, color: theme.text, minWidth: 220 }}>
          Region: {region.name}
        </span>
        <Pill theme={theme} kind="info" small>{fmt.int(stats.salon_count)} salon(ów)</Pill>
        <Pill theme={theme} kind={stats.sales_in_period > 0 ? 'good' : 'neutral'} small>
          Sprzedaż okres: {fmt.int(stats.sales_in_period)}
        </Pill>
        {stats.sales_current_month != null && (
          <Pill theme={theme} kind="neutral" small>MTD: {fmt.int(stats.sales_current_month)}</Pill>
        )}
        <div style={{ flex: 1 }} />
        {onSendScope && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onSendScope('region', region.name) }}
            title="Wyślij raport regionu"
            style={{ color: theme.textSub, display: 'inline-flex', cursor: 'pointer', padding: 4, borderRadius: 6 }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.accent}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.textSub}
          ><Icon.mail /></span>
        )}
        <span style={{
          color: theme.textDim, display: 'inline-flex',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 150ms',
        }}><Icon.chevron /></span>
      </button>
      {expanded && (
        <>
          {region.districts && region.districts.length > 0 ? (
            region.districts.map(d => (
              <DistrictGroup key={d.name}
                theme={theme}
                district={d}
                expanded={!!openDistricts[`${region.name}::${d.name}`]}
                onToggle={() => toggleDistrict(d.name)}
                columns={columns}
                onSendScope={onSendScope}
              />
            ))
          ) : region.salons ? (
            region.salons.length === 0
              ? <EmptyRow theme={theme}>Brak salonów.</EmptyRow>
              : <SalonTable theme={theme} columns={columns} rows={region.salons} totalLabel="Razem region" />
          ) : null}
        </>
      )}
    </div>
  )
}

// ─ District (level 3) ──────────────────────────────────────────
function DistrictGroup({ theme, district, expanded, onToggle, columns, onSendScope }) {
  const stats = district.stats || {}
  return (
    <div>
      <button onClick={onToggle} style={{
        width: '100%',
        padding: '8px 18px 8px 56px',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        background: theme.surfaceSub, border: 'none',
        cursor: 'pointer', textAlign: 'left',
        borderTop: `1px solid ${theme.border}`,
        borderBottom: expanded ? `1px solid ${theme.border}` : 'none',
        fontFamily: T.fontUI,
      }}>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: theme.text, minWidth: 220 }}>
          🏙 Dystrykt: {district.name}
        </span>
        <Pill theme={theme} kind="info" small>{fmt.int(stats.salon_count)} salon(ów)</Pill>
        <Pill theme={theme} kind={stats.sales_in_period > 0 ? 'good' : 'neutral'} small>
          Sprzedaż okres: {fmt.int(stats.sales_in_period)}
        </Pill>
        {stats.sales_current_month != null && (
          <Pill theme={theme} kind="neutral" small>MTD: {fmt.int(stats.sales_current_month)}</Pill>
        )}
        <div style={{ flex: 1 }} />
        {onSendScope && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onSendScope('district', district.name) }}
            title="Wyślij raport dystryktu"
            style={{ color: theme.textSub, display: 'inline-flex', cursor: 'pointer', padding: 4, borderRadius: 6 }}
            onMouseEnter={(e) => e.currentTarget.style.color = theme.accent}
            onMouseLeave={(e) => e.currentTarget.style.color = theme.textSub}
          ><Icon.mail /></span>
        )}
        <span style={{
          color: theme.textDim, display: 'inline-flex',
          transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
          transition: 'transform 150ms',
        }}><Icon.chevron /></span>
      </button>
      {expanded && (
        district.salons.length === 0
          ? <EmptyRow theme={theme}>Brak salonów.</EmptyRow>
          : <SalonTable theme={theme} columns={columns} rows={district.salons} totalLabel="Razem dystrykt" />
      )}
    </div>
  )
}

function EmptyRow({ theme, children }) {
  return (
    <div style={{
      padding: '24px 18px', textAlign: 'center',
      color: theme.textDim, fontSize: 12.5,
    }}>{children}</div>
  )
}

// ─ Salon table (level 4 — leaf) ─────────────────────────────────
function SalonTable({ theme, columns, rows, totalLabel = 'Razem' }) {
  const cols = columns.map(c => normalizeColumn(c))
  const gridTemplate = cols.map(c => c.gridSize).join(' ')
  const total = computeTotals(cols, rows)

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'grid', gridTemplateColumns: gridTemplate,
        padding: '8px 18px 8px 60px', gap: 12,
        background: theme.bgSub,
        fontSize: 10, fontWeight: 700, color: theme.textDim,
        letterSpacing: 0.5, textTransform: 'uppercase',
        borderBottom: `1px solid ${theme.border}`,
        fontFamily: T.fontUI,
      }}>
        {cols.map(c => (
          <span key={c.field} style={{
            textAlign: c.align,
            display: 'flex', alignItems: 'center',
            justifyContent: c.align === 'right' ? 'flex-end' : 'flex-start',
          }}>{c.header}</span>
        ))}
      </div>
      {/* Rows */}
      {rows.map((row, i) => {
        const hasSale = cols.some(c => c.sales && Number(row[c.field]) > 0)
        return (
          <div key={row.id ?? row.sfid ?? row.code ?? row.plant_code ?? i}
            style={{
              display: 'grid', gridTemplateColumns: gridTemplate,
              padding: '10px 18px 10px 60px', gap: 12,
              fontSize: 12.5,
              alignItems: 'center',
              background: hasSale ? `${theme.good}10` : 'transparent',
              borderBottom: `1px solid ${theme.border}`,
              transition: 'background 100ms',
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = theme.bgSub}
            onMouseLeave={(e) => e.currentTarget.style.background = hasSale ? `${theme.good}10` : 'transparent'}
          >
            {cols.map(c => (
              <Cell key={c.field} col={c} row={row} theme={theme} />
            ))}
          </div>
        )
      })}
      {/* Total row */}
      {total && (
        <div style={{
          display: 'grid', gridTemplateColumns: gridTemplate,
          padding: '10px 18px 10px 60px', gap: 12,
          fontSize: 12.5,
          alignItems: 'center',
          background: theme.bgSub,
          fontFamily: T.fontMono, fontWeight: 700,
        }}>
          {cols.map((c, idx) => {
            // Place "Razem" label on the first non-numeric column we span over.
            if (c.numeric || c.sales) {
              const v = total[c.field]
              return <Cell key={c.field} col={c} row={{ [c.field]: v }} theme={theme} bold />
            }
            // First text col → label only
            if (idx === 0) {
              return (
                <span key={c.field} style={{
                  color: theme.textDim, fontFamily: T.fontUI, fontSize: 11,
                  fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                }}>{totalLabel}</span>
              )
            }
            return <span key={c.field} />
          })}
        </div>
      )}
    </div>
  )
}

function Cell({ col, row, theme, bold = false }) {
  const value = row[col.field]
  const align = col.align
  const isMono = col.numeric || col.sales || col.phone
  const baseStyle = {
    textAlign: align,
    display: align === 'right' ? 'flex' : 'block',
    justifyContent: align === 'right' ? 'flex-end' : undefined,
    fontFamily: isMono ? T.fontMono : T.fontUI,
    color: theme.text,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  }
  if (col.render) {
    return <span style={baseStyle}>{col.render(value, row)}</span>
  }
  if (col.sales) {
    const isZero = !value || Number(value) === 0
    return (
      <span style={{
        ...baseStyle,
        color: isZero ? theme.textDim : theme.good,
        fontWeight: bold ? 700 : (isZero ? 400 : 600),
      }}>{value == null ? '—' : fmt.int(value)}</span>
    )
  }
  if (col.phone) {
    if (!value) return <span style={{ ...baseStyle, color: theme.textDim }}>—</span>
    return (
      <a href={`tel:${value}`} style={{
        ...baseStyle,
        color: theme.accent, textDecoration: 'underline',
      }}>{value}</a>
    )
  }
  if (col.numeric) {
    return (
      <span style={{
        ...baseStyle,
        color: value == null ? theme.textDim : theme.text,
        fontWeight: bold ? 700 : 500,
      }}>{value == null ? '—' : fmt.int(value)}</span>
    )
  }
  // Plain text
  return (
    <span style={{
      ...baseStyle,
      color: value == null || value === '' ? theme.textDim : theme.text,
      fontWeight: 500,
    }}>{value == null || value === '' ? '—' : String(value)}</span>
  )
}

function normalizeColumn(c) {
  const numeric = !!(c.numeric || c.sales)
  const align = c.align ?? (numeric ? 'right' : 'left')
  let gridSize
  if (typeof c.width === 'number') gridSize = `${c.width}px`
  else if (typeof c.width === 'string') gridSize = c.width
  else gridSize = '1fr'
  return { ...c, align, gridSize }
}

function computeTotals(cols, rows) {
  if (!rows || rows.length === 0) return null
  const out = {}
  let any = false
  for (const c of cols) {
    if (c.sales || c.numeric) {
      out[c.field] = rows.reduce((a, r) => a + (Number(r[c.field]) || 0), 0)
      any = true
    }
  }
  return any ? out : null
}
